import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Button, Input } from "@vimos/ui";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../lib/auth";
import { notifyCmsChanged } from "../../lib/cms-webhook";
import type { MediaItem } from "@vimos/api-client";
import { fetchMediaItems, mediaBucketName, mediaPublicUrl, uploadMediaFile } from "./mediaUtils";

export function MediaPage() {
  const queryClient = useQueryClient();
  const { profile, session } = useAuth();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [altText, setAltText] = useState("");
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const { data: media, isLoading, error } = useQuery({ queryKey: ["media"], queryFn: fetchMediaItems });

  const uploadMedia = useMutation({
    mutationFn: async () => {
      setUploadError(null);
      const file = fileInputRef.current?.files?.[0];
      if (!file) throw new Error("Selecciona una imagen.");
      const savedItem = await uploadMediaFile(file, altText, profile?.id);
      return savedItem;
    },
    onSuccess: async (savedItem) => {
      setAltText("");
      if (fileInputRef.current) fileInputRef.current.value = "";
      queryClient.invalidateQueries({ queryKey: ["media"] });

      try {
        await notifyCmsChanged("media", "insert", savedItem.id, session?.access_token);
      } catch (webhookError) {
        console.error("Error al notificar cambio CMS", webhookError);
      }
    },
    onError: (uploadIssue) => {
      setUploadError(uploadIssue instanceof Error ? uploadIssue.message : "No pudimos subir la imagen.");
    },
  });

  const deleteMedia = useMutation({
    mutationFn: async (item: MediaItem) => {
      setDeleteError(null);
      const imageUrl = mediaPublicUrl(item);
      const referenced = await isMediaReferenced(imageUrl);
      if (referenced) {
        throw new Error("Esta imagen esta referenciada por contenido. Reemplazala antes de eliminarla.");
      }
      const { error: storageError } = await supabase.storage
        .from(mediaBucketName)
        .remove([item.storage_path]);
      if (storageError) throw storageError;
      const { error: deleteError } = await supabase.from("media").delete().eq("id", item.id);
      if (deleteError) throw deleteError;
      return item.id;
    },
    onSuccess: async (deletedId: string) => {
      queryClient.invalidateQueries({ queryKey: ["media"] });

      try {
        await notifyCmsChanged("media", "delete", deletedId, session?.access_token);
      } catch (webhookError) {
        console.error("Error al notificar cambio CMS", webhookError);
      }
    },
    onError: (deleteIssue) => {
      setDeleteError(deleteIssue instanceof Error ? deleteIssue.message : "No pudimos eliminar esta imagen.");
    },
  });

  async function isMediaReferenced(imageUrl: string): Promise<boolean> {
    const checks = await Promise.allSettled([
      supabase.from("pages").select("id").eq("og_image_url", imageUrl).limit(1),
      supabase.from("portfolio_items").select("id").eq("cover_image_url", imageUrl).limit(1),
      supabase.from("case_studies").select("id").eq("cover_image_url", imageUrl).limit(1),
      supabase.from("blog_posts").select("id").eq("cover_image_url", imageUrl).limit(1),
    ]);

    return checks.some((check) => {
      if (check.status !== "fulfilled" || check.value.error) return false;
      return Boolean(check.value.data?.length);
    });
  }

  return (
    <div>
      <div>
        <h1 className="font-display text-ink text-2xl font-bold">Media</h1>
        <p className="text-mist mt-1 text-sm">
          Sube imagenes al bucket media y administra su metadata basica.
        </p>
      </div>

      <form
        className="border-sand/40 bg-paper shadow-card mt-6 grid gap-4 rounded-lg border p-5 md:grid-cols-[1fr_1fr_auto]"
        onSubmit={(event) => {
          event.preventDefault();
          uploadMedia.mutate();
        }}
      >
        <div className="flex flex-col gap-1.5">
          <label className="font-body text-ink text-sm font-medium">Imagen</label>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
            className="border-sand/70 bg-paper text-ink h-11 rounded-md border px-3 py-2 text-sm"
          />
          <p className="text-mist text-sm">Maximo 5 MB.</p>
        </div>
        <Input label="Alt text" value={altText} onChange={(event) => setAltText(event.target.value)} />
        <div className="flex items-end">
          <Button type="submit" isLoading={uploadMedia.isPending}>
            Subir imagen
          </Button>
        </div>
        {uploadError ? <p className="text-sm text-red-600 md:col-span-3">{uploadError}</p> : null}
      </form>

      {isLoading && <p className="text-mist mt-6">Cargando...</p>}
      {error && <p className="mt-6 text-red-600">No pudimos cargar media.</p>}
      {deleteError ? (
        <p
          role="alert"
          className="mt-6 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          {deleteError}
        </p>
      ) : null}

      <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {media?.map((item) => {
          const imageUrl = mediaPublicUrl(item);
          return (
            <article
              key={item.id}
              className="border-sand/40 bg-paper shadow-card overflow-hidden rounded-lg border"
            >
              <div className="bg-ink/5 aspect-[16/10]">
                <img
                  src={imageUrl}
                  alt={item.alt_text ?? ""}
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="p-4">
                <p className="text-ink truncate text-sm font-medium">{item.file_name ?? item.storage_path}</p>
                <p className="text-mist mt-1 truncate text-xs">{item.alt_text ?? "Sin alt text"}</p>
                <p className="text-mist mt-1 text-xs">
                  {item.mime_type ?? "image/*"}{" "}
                  {item.size_bytes ? `· ${(item.size_bytes / 1024).toFixed(1)} KB` : ""}
                </p>
                <p className="text-mist mt-1 truncate text-xs">{imageUrl}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Link to={`/media/${item.id}`}>
                    <Button type="button" size="sm" variant="outline">
                      Editar
                    </Button>
                  </Link>
                  <Button
                    type="button"
                    size="sm"
                    variant="danger"
                    onClick={() => {
                      if (window.confirm("Eliminar esta imagen?")) deleteMedia.mutate(item);
                    }}
                  >
                    Eliminar
                  </Button>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
